/** PROPS */
import BASE_INPUT_DEFAULT_PROPS from '@/components/base_components/generic-relations-wrapper/configuration/BASE_INPUT_DEFAULT_PROPS';

/** HELPERS */
import i18n from '@/language/i18n'
/** COMPONENTS **/
import { RelatedUnit } from '@/components/base_components/generic-relations-wrapper/types/RelatedUnit';
import { RelatedInput } from '@/components/base_components/generic-relations-wrapper/types/RelatedInput';

import WorkProjectPicker from '@/components/global_components/pickers/project_picker/WorkProjectPicker';
import WorkFormAutocompleteLazy from '@/components/base_components/WorkAutocompleteLazy';
import ProductPicker from '@/components/global_components/pickers/product_picker/ProductPicker';
import DevicePicker from '@/components/global_components/pickers/device_picker/DevicePicker';
import WorkContactPicker from '@/components/global_components/pickers/contact_picker/WorkContactPicker';
import WorkCategoryTreeFilter from '@/components/global_components/filters/category_filter/WorkCategoryTreeFilter';
import WorkCategoryAttributeFilter from '@/components/global_components/filters/category_attribute_filter/WorkCategoryAttributeFilter';
import WorkflowMultiActionSelectWrapper from '@/components/base_components/workflow/workflow_inputs/workflow_action_picker/WorkflowMultiActionSelectWrapper';
import projectService from '@/modules/projects/shared_services/project-service'
/********************************************************
 * Override these file with customer implementation to  *
 * customize Messages                                   *
 *******************************************************/
const handlePromiseBasedProject = async (ids) => {
  const idList = Array.isArray(ids) ? ids : [ids];
  const results = [];

  for (const id of idList) {
    try {
      let response = await projectService.getProjectById(id);
      results.push(response.data?.data?.customerContact?.id);
    } catch (e) {
      throw e;
    }
  }

    return results.length === 1 ? results[0] : results;
};

const CONFIG = Object.freeze([
  new RelatedUnit(
    i18n.t('Common.projects'),
    [
      new RelatedInput(1, WorkProjectPicker, 'project', {
        label: i18n.t('Common.project'),
        itemText: 'data.label',
        itemValue: 'id',
        vid: 'projects',
        getData: (response) => response.data.data,
        ...BASE_INPUT_DEFAULT_PROPS,
        onDataChange: {
          handlePromiseBasedDataChanged: handlePromiseBasedProject,
          targetModel: 'contact',
        },
      },8),
      new RelatedInput(
        2,
        WorkCategoryTreeFilter,
        'projectattributes',
        {
          label: i18n.t('Common.characteristics'),
          itemValue: 'id',
          vid: 'projectattributes',
          hasFilterOption: false,
          selectParents: true,
          path: 'attributecategories/treehierarchy/projects',
          ...BASE_INPUT_DEFAULT_PROPS,
          onlyFirstChild: false,
        },
        4,
        false
      ),
      new RelatedInput(
        2,
        WorkCategoryAttributeFilter,
        'projectattributevalues',
        {
          label: i18n.t('Common.attribute'),
          vid: 'projectattributevalues',
          withFalseValue: false,
          path: 'attributecategories/attributes/projects',
          ...BASE_INPUT_DEFAULT_PROPS,
        },
        4,
        false
      ),
    ],
  ),
  new RelatedUnit(i18n.t('Common.contacts'), [
    new RelatedInput(
      1,
      WorkContactPicker,
      'contact',
      {
        label: i18n.t('Common.contact'),
        path: 'contacts',
        itemText: 'data.shortCaption',
        itemValue: 'id',
        resultPageSize: 500,
        showableInputItemsSize: 100,
        vid: 'contacts',
        getData: (response) => response.data.data,
        cacheItems: false,
        mandatoryFilters: { },
        dialogTitle: i18n.t('Common.contact'),
        ...BASE_INPUT_DEFAULT_PROPS,
      },
      8
    ),
    new RelatedInput(
      2,
      WorkCategoryTreeFilter,
      'contactattributes',
      {
        label: i18n.t('Common.characteristics'),
        itemValue: 'id',
        vid: 'contactattributes',
        hasFilterOption: false,
        selectParents: true,
        path: 'attributecategories/treehierarchy/contacts',
        ...BASE_INPUT_DEFAULT_PROPS,
        onlyFirstChild: false,
      },
      4,
      false
    ),
    new RelatedInput(
      2,
      WorkCategoryAttributeFilter,
      'contactattributevalues',
      {
        label: i18n.t('Common.attribute'),
        vid: 'contactattributevalues',
        withFalseValue: false,
        path: 'attributecategories/attributes/contacts',
        ...BASE_INPUT_DEFAULT_PROPS,
      },
      4,
      false
    ),
  ]),
  new RelatedUnit(
    i18n.t('Common.workflowSteps'),
    [
      new RelatedInput(1, WorkflowMultiActionSelectWrapper, 'workflowaction', {
        label: i18n.t('Common.workflow'),
        vid: 'workflowaction',
        withFormField: false,
        ...BASE_INPUT_DEFAULT_PROPS,
      }),
    ],
    { cols: '12', md: '6', lg: '3' }
  ),
  new RelatedUnit(
    i18n.t('Common.products'),
    [
      new RelatedInput(
        1,
        WorkCategoryTreeFilter,
        'category',
        {
          label: i18n.t('Common.category'),
          itemValue: 'id',
          vid: 'categories',
          hasFilterOption: false,
          selectParents: true,
          ...BASE_INPUT_DEFAULT_PROPS,
          onlyFirstChild: false,
        },
        4,
        false
      ),
      new RelatedInput(
        1,
        WorkCategoryAttributeFilter,
        'productattribute',
        {
          label: i18n.t('Common.attribute'),
          vid: 'attributes',
          withFalseValue: false,
          ...BASE_INPUT_DEFAULT_PROPS,
        },
        4,
        false
      ),
      new RelatedInput(
        2,
        ProductPicker,
        'product',
        {
          label: i18n.t('Common.product'),
          path: 'products',
          itemText: 'label',
          itemValue: 'id',
          vid: 'products',
          ...BASE_INPUT_DEFAULT_PROPS,
        },
        4
      ),
      new RelatedInput(
        3,
        DevicePicker,
        'device',
        {
          label: i18n.t('Common.device'),
          path: 'devices',
          itemText: 'label',
          itemValue: 'id',
          vid: 'devices',
          createDeviceButton: false,
          ...BASE_INPUT_DEFAULT_PROPS,
        },
        4
      ),
    ],
    { cols: '12', md: '9' }
  ),
  new RelatedUnit(
    i18n.t('ModuleRegistry.groups.personal.name'),
    [
      new RelatedInput(1, WorkFormAutocompleteLazy, 
        'employees', 
        { 
          label: i18n.t('Common.employee'),
          mandatoryFilters: { isDeleted: false },
          path:"employees",
          getData: (response) => response.data.data,
          itemText:"data.fullname",
          itemValue:"id",
          ...BASE_INPUT_DEFAULT_PROPS,
        }
      ),
      new RelatedInput(2, WorkFormAutocompleteLazy, 
        'occupationgroups', 
        {
          label: i18n.t('Common.occupationGroup'),
          path: 'occupationgroups',
          getData: (response) => response.data.data,
          itemText:"data.label",
          itemValue: "id",
          vid:"occupationGroup",
          ...BASE_INPUT_DEFAULT_PROPS,
        }
      ),
    ],
    { cols: '12', md: '3' }
  ),
]);

export default CONFIG;

